import {SET_ERROR} from "../Redux/Actions/Types/errorActionTypes";
import {useDispatch} from "react-redux";

export const useShowPermissionDenied = () => {
    const dispatch = useDispatch();

    return () => {
        dispatch({
            type: SET_ERROR,
            error: {
                Code: 403,
                Message: 'Permission Denied',
                Title: 'Forbidden',
            }
        });
    };
};

// Optimistic-concurrency rejections (App\Exception\ConflictException) carry a
// machine-readable body: {code: 'conflict', conflicts: [{entity, key?, label?}]}.
// Handlers discriminate on that contract — never on the human-facing message text,
// which is display copy and free to change.
const conflictsOf = (error) => {
    const body = error?.response?.data ?? error?.xhr?.data;
    if (body?.code !== 'conflict') {
        return [];
    }
    return body.conflicts ?? [];
};

const conflictEntitiesOf = (error) => conflictsOf(error).map((conflict) => conflict?.entity);

// SB-3308: saving modeled uplift from a snapshot that another source (e.g. a
// Fulcrum sync) has changed since the page loaded is rejected with 409 and the
// whole save is rolled back. This handler refreshes the modeled uplift via the
// caller-provided reload (edits held in component state are preserved) and tells
// the user to review the new values and save again. Falls back to the default
// error handler for anything that isn't this conflict.
export const useModeledUpliftConflictHandler = () => {
    const dispatch = useDispatch();
    const defaultErrorHandler = useDefaultErrorHandler();

    return (error, reloadModeledUplift) => {
        if (conflictEntitiesOf(error).includes('ModeledUplift')) {
            reloadModeledUplift?.();
            dispatch({
                type: SET_ERROR,
                error: {
                    Code: 409,
                    Title: 'Modeled Uplift Changed',
                    Message: (error?.message ?? 'The Modeled Uplift was changed by another source.') +
                        ' The latest values have been loaded — your other edits on this page are preserved.',
                }
            });
            return;
        }
        defaultErrorHandler(error);
    };
};

// Saving survey answers from a snapshot that another source (an import,
// another user) has changed since the form loaded is rejected with 409 and the
// whole save is rolled back. The caller-provided reload-merge refetches the
// authoritative answers and re-applies the user's unsaved edits on top — never a
// blind retry, because the save is full-replace and re-sending the stale payload
// would clobber the background change. Falls through to the modeled-uplift 409
// handler (the same PUT can also trip that guard), then the default handler.
export const useSurveyAnswerConflictHandler = () => {
    const dispatch = useDispatch();
    const handleModeledUpliftConflict = useModeledUpliftConflictHandler();

    return (error, reloadAndMergeAnswers, reloadModeledUplift) => {
        if (conflictEntitiesOf(error).includes('SurveyAnswer')) {
            reloadAndMergeAnswers?.();
            const conflictedFieldNames = conflictsOf(error)
                .filter((conflict) => conflict?.entity === 'SurveyAnswer')
                .map((conflict) => conflict?.label ?? conflict?.key)
                .filter(Boolean);
            dispatch({
                type: SET_ERROR,
                error: {
                    Code: 409,
                    Title: 'Possible Edit Conflict',
                    Message: 'Some saved answers have changed since this page was loaded.' +
                        (conflictedFieldNames.length ?
                            ' Please review the following fields and then save again:' :
                            ' Please review your changes and save again.'),
                    Details: conflictedFieldNames,
                }
            });
            return;
        }
        handleModeledUpliftConflict(error, reloadModeledUplift);
    };
};

export const useDefaultErrorHandler = () => {
    const dispatch = useDispatch();

    return (error) => {
        if (error?.message || error?.response) {
            const response = error?.xhr ?? error?.response;
            const message = error?.message ?? 'Unexpected error';
            const stackTrace = error?.data?.stackTrace ?? response?.data?.stackTrace;

            dispatch({
                type: SET_ERROR,
                error: {
                    Code: response?.status ?? null,
                    Message: message,
                    Title: response?.statusText ?? 'Error',
                    StackTrace: stackTrace,
                }
            });
        } else if (error instanceof Error) {
            throw error;
        }
    }
};
